import { useEffect, useMemo, useRef } from 'react';
import maplibregl, { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import type { Feature } from 'geojson';
import type { Course, FleetPosition } from '../lib/types';
import { basemapStyle } from '../lib/basemap';
import { circlePolygon, emptyFc, fc } from '../lib/shapes';
import { distanceM } from '../lib/geo';

interface Props {
  course: Course;
  positions: Record<string, FleetPosition>;
  status: 'connecting' | 'open' | 'offline';
}

const OFFLINE_AFTER = 90_000;

/** Pro-shop view: every buggy on one map, plus an exceptions list. */
export default function FleetScreen({ course, positions, status }: Props) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const ready = useRef(false);

  const list = useMemo(() => {
    const now = Date.now();
    return Object.values(positions)
      .map((p) => {
        const stale = now - p.ts > OFFLINE_AFTER;
        const nearest = course.holes.reduce(
          (best, h) => {
            const d = distanceM([p.lng, p.lat], h.green.middle);
            return d < best.d ? { hole: h.number, d } : best;
          },
          { hole: 0, d: Infinity }
        );
        return { ...p, stale, nearestHole: nearest.hole, nearestM: Math.round(nearest.d) };
      })
      .sort((a, b) => {
        const rank = (x: typeof a) => (x.alert ? 0 : x.stale ? 1 : 2);
        return rank(a) - rank(b) || a.buggyId.localeCompare(b.buggyId);
      });
  }, [positions, course.holes]);

  useEffect(() => {
    if (!el.current || map.current) return;
    const m = new maplibregl.Map({
      container: el.current,
      style: basemapStyle(),
      center: course.centre,
      zoom: 14.4,
      attributionControl: { compact: true },
    });
    map.current = m;
    m.on('load', () => {
      m.addSource('zones', { type: 'geojson', data: emptyFc });
      m.addSource('buggies', { type: 'geojson', data: emptyFc });
      m.addLayer({
        id: 'zones-fill',
        type: 'fill',
        source: 'zones',
        paint: { 'fill-color': '#38e07b', 'fill-opacity': 0.12 },
      });
      m.addLayer({
        id: 'buggy-dot',
        type: 'circle',
        source: 'buggies',
        paint: {
          'circle-radius': 9,
          'circle-color': [
            'case',
            ['get', 'alert'], '#ff3b3b',
            ['get', 'stale'], '#6b7c72',
            '#38e07b',
          ],
          'circle-stroke-color': '#0b120d',
          'circle-stroke-width': 2,
        },
      });
      m.addLayer({
        id: 'buggy-label',
        type: 'symbol',
        source: 'buggies',
        layout: { 'text-field': ['get', 'buggyId'], 'text-size': 12, 'text-offset': [0, 1.4] },
        paint: { 'text-color': '#eaf3ec', 'text-halo-color': '#0b120d', 'text-halo-width': 1.6 },
      });
      (m.getSource('zones') as GeoJSONSource).setData(
        fc(course.keepOut.filter((z) => z.severity === 'block').map((z) => circlePolygon(z.centre, z.radius)))
      );
      ready.current = true;
    });
    return () => {
      ready.current = false;
      m.remove();
      map.current = null;
    };
  }, [course]);

  useEffect(() => {
    const m = map.current;
    if (!m || !ready.current) return;
    (m.getSource('buggies') as GeoJSONSource | undefined)?.setData(
      fc(
        list.map<Feature>((p) => ({
          type: 'Feature',
          properties: { buggyId: p.buggyId, alert: !!p.alert, stale: p.stale },
          geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
        }))
      )
    );
  }, [list]);

  const alerts = list.filter((p) => p.alert || p.stale);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 34%) 1fr', height: '100%' }}>
      <div className="screen" style={{ borderRight: '1px solid var(--line)' }}>
        <div className="card">
          <h2>FLEET</h2>
          <div className="row">
            <span className={`pill ${status === 'open' ? 'ok' : status === 'offline' ? 'bad' : 'warn'}`}>
              {status.toUpperCase()}
            </span>
            <span className="pill">{list.length} UNITS</span>
            <span className={`pill ${alerts.length ? 'bad' : 'ok'}`}>{alerts.length} ALERTS</span>
          </div>
        </div>

        {alerts.length > 0 && (
          <div className="card">
            <h2>NEEDS ATTENTION</h2>
            <table className="grid">
              <tbody>
                {alerts.map((p) => (
                  <tr key={p.buggyId}>
                    <td className="name">{p.buggyId}</td>
                    <td style={{ color: p.alert ? 'var(--danger)' : 'var(--muted)' }}>
                      {p.alert === 'keepout'
                        ? 'ON A GREEN'
                        : p.alert === 'speeding'
                          ? 'SPEEDING'
                          : p.stale
                            ? `NO SIGNAL ${Math.round((Date.now() - p.ts) / 60000)}m`
                            : String(p.alert)}
                    </td>
                    <td>H{p.nearestHole}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="card">
          <h2>ALL UNITS</h2>
          <table className="grid">
            <thead>
              <tr>
                <th className="name">Buggy</th>
                <th>Near</th>
                <th>Speed</th>
                <th>Batt</th>
                <th>Seen</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.buggyId} style={{ opacity: p.stale ? 0.5 : 1 }}>
                  <td className="name">{p.buggyId}</td>
                  <td>H{p.nearestHole}</td>
                  <td>{(p.speed * 3.6).toFixed(0)} km/h</td>
                  <td>{p.battery != null ? `${p.battery}%` : '—'}</td>
                  <td>{Math.round((Date.now() - p.ts) / 1000)}s</td>
                </tr>
              ))}
              {!list.length && (
                <tr>
                  <td colSpan={5} className="muted">
                    No buggies reporting yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ position: 'relative' }}>
        <div ref={el} className="map" />
      </div>
    </div>
  );
}
